import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, Plus, Eye, ShieldAlert, MessageSquarePlus, ClipboardCheck, Search,
} from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import userService from '../../../services/user';
import type { UserData } from '../../../services/user';
import type {
  WhsReport, ReportStatus, InvestigationMethod, InvestigationStatus,
  WhsAction, ActionType, ActionPriority, ActionStatus, HierarchyControl, UnmaskResult,
} from '../../../types/whs';
import {
  StatusBadge, SeverityBadge, Badge,
  REPORT_TYPE_LABEL, SEVERITY_LABEL, TREATMENT_LABEL, REPORT_STATUS_LABEL,
  INVESTIGATION_METHOD_LABEL, ACTION_STATUS_LABEL, ACTION_STATUS_CLASS,
  PRIORITY_LABEL, PRIORITY_CLASS, ACTION_TYPE_LABEL, HIERARCHY_LABEL,
} from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatLocalDateInTz, formatDateTimeInTz } from '../../../utils/timezone';

const REPORT_STATUSES = Object.keys(REPORT_STATUS_LABEL) as ReportStatus[];
const INV_METHODS = Object.keys(INVESTIGATION_METHOD_LABEL) as InvestigationMethod[];
const INV_STATUSES: InvestigationStatus[] = ['open', 'in_progress', 'completed'];
const ACTION_TYPES = Object.keys(ACTION_TYPE_LABEL) as ActionType[];
const ACTION_PRIORITIES = Object.keys(PRIORITY_LABEL) as ActionPriority[];
const HIERARCHY_KEYS = Object.keys(HIERARCHY_LABEL) as HierarchyControl[];

const inputCls =
  'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none';
const labelCls = 'block text-xs font-medium text-neutral-600 mb-1';
const cardCls = 'rounded-xl border border-neutral-200 bg-white p-5';

function userLabel(u: UserData): string {
  return [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username;
}

function actionAssignee(a: WhsAction): string {
  return [a.assignee_first_name, a.assignee_last_name].filter(Boolean).join(' ').trim() || 'Unassigned';
}

function WhsReportDetailInner() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const { id } = useParams<{ id: string }>();
  const reportId = Number(id);

  const [report, setReport] = useState<WhsReport | null>(null);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Management panel
  const [statusValue, setStatusValue] = useState<ReportStatus>('submitted');
  const [statusNote, setStatusNote] = useState('');
  const [statusVisible, setStatusVisible] = useState(false);
  const [assignedTo, setAssignedTo] = useState('');

  // Add note
  const [noteBody, setNoteBody] = useState('');
  const [noteVisible, setNoteVisible] = useState(false);

  // Investigation
  const [invMethod, setInvMethod] = useState<InvestigationMethod>('icam');
  const [invImmediate, setInvImmediate] = useState('');
  const [invContributing, setInvContributing] = useState('');
  const [invRoot, setInvRoot] = useState('');
  const [invFindings, setInvFindings] = useState('');
  const [invLessons, setInvLessons] = useState('');
  const [invStatus, setInvStatus] = useState<InvestigationStatus>('open');

  // Add action
  const [showActionForm, setShowActionForm] = useState(false);
  const [aTitle, setATitle] = useState('');
  const [aDesc, setADesc] = useState('');
  const [aType, setAType] = useState<ActionType>('corrective');
  const [aPriority, setAPriority] = useState<ActionPriority>('medium');
  const [aHierarchy, setAHierarchy] = useState<HierarchyControl | ''>('');
  const [aDue, setADue] = useState('');
  const [aAssigned, setAAssigned] = useState('');

  // Unmask
  const [unmaskConfirming, setUnmaskConfirming] = useState(false);
  const [unmasking, setUnmasking] = useState(false);
  const [unmaskResult, setUnmaskResult] = useState<UnmaskResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await whsService.getReport(reportId);
    if (res.success) setReport(res.data);
    else setError(res.error.message);
    setLoading(false);
  }, [reportId]);

  const loadUsers = useCallback(async () => {
    const res = await userService.getUsers();
    if (res.success) setUsers(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Sync editable panels from the loaded report.
  useEffect(() => {
    if (!report) return;
    setStatusValue(report.status);
    setAssignedTo(report.assigned_to != null ? String(report.assigned_to) : '');
    const inv = report.investigation;
    if (inv) {
      setInvMethod(inv.method);
      setInvImmediate(inv.immediate_causes ?? '');
      setInvContributing(inv.contributing_factors ?? '');
      setInvRoot(inv.root_causes ?? '');
      setInvFindings(inv.findings ?? '');
      setInvLessons(inv.lessons_learned ?? '');
      setInvStatus(inv.status);
    }
  }, [report]);

  const fmtDateTime = (v: string | null | undefined) => (v ? formatDateTimeInTz(v, orgTz) : '—');
  const fmtDate = (v: string | null | undefined) => (v ? formatLocalDateInTz(v, orgTz) : '—');

  const saveStatus = async () => {
    setSaving(true);
    const res = await whsService.updateReport(reportId, {
      status: statusValue,
      status_note: statusNote.trim() || undefined,
      status_visible_to_reporter: statusVisible,
    });
    if (res.success) { setStatusNote(''); setStatusVisible(false); await load(); }
    else setError(res.error.message);
    setSaving(false);
  };

  const saveAssignee = async () => {
    setSaving(true);
    const res = await whsService.updateReport(reportId, {
      assigned_to: assignedTo ? Number(assignedTo) : null,
    });
    if (res.success) await load();
    else setError(res.error.message);
    setSaving(false);
  };

  const toggleNotifiable = async (checked: boolean) => {
    setSaving(true);
    const res = await whsService.updateReport(reportId, { is_notifiable: checked });
    if (res.success) await load();
    else setError(res.error.message);
    setSaving(false);
  };

  const markNotified = async () => {
    setSaving(true);
    const res = await whsService.updateReport(reportId, { notified_regulator_at: new Date().toISOString() });
    if (res.success) await load();
    else setError(res.error.message);
    setSaving(false);
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setSaving(true);
    const res = await whsService.addReportUpdate(reportId, {
      body: noteBody.trim(),
      visible_to_reporter: noteVisible,
    });
    if (res.success) { setNoteBody(''); setNoteVisible(false); await load(); }
    else setError(res.error.message);
    setSaving(false);
  };

  const startInvestigation = async () => {
    setSaving(true);
    const res = await whsService.createInvestigation({ report_id: reportId, method: 'icam' });
    if (res.success) await load();
    else setError(res.error.message);
    setSaving(false);
  };

  const saveInvestigation = async () => {
    if (!report?.investigation) return;
    setSaving(true);
    const res = await whsService.updateInvestigation(report.investigation.id, {
      method: invMethod,
      immediate_causes: invImmediate.trim() || null,
      contributing_factors: invContributing.trim() || null,
      root_causes: invRoot.trim() || null,
      findings: invFindings.trim() || null,
      lessons_learned: invLessons.trim() || null,
      status: invStatus,
    });
    if (res.success) await load();
    else setError(res.error.message);
    setSaving(false);
  };

  const createAction = async () => {
    if (!aTitle.trim()) return;
    setSaving(true);
    const res = await whsService.createAction({
      source_type: 'report',
      source_id: reportId,
      title: aTitle.trim(),
      description: aDesc.trim() || null,
      action_type: aType,
      priority: aPriority,
      hierarchy_control: aHierarchy || null,
      due_date: aDue || null,
      assigned_to: aAssigned ? Number(aAssigned) : null,
    });
    if (res.success) {
      setShowActionForm(false);
      setATitle(''); setADesc(''); setAType('corrective'); setAPriority('medium');
      setAHierarchy(''); setADue(''); setAAssigned('');
      await load();
    } else setError(res.error.message);
    setSaving(false);
  };

  const setActionStatus = async (actionId: number, status: ActionStatus) => {
    setSaving(true);
    const res = await whsService.updateAction(actionId, { status });
    if (res.success) await load();
    else setError(res.error.message);
    setSaving(false);
  };

  const revealReporter = async () => {
    setUnmasking(true);
    const res = await whsService.unmaskReporter(reportId);
    if (res.success) setUnmaskResult(res.data);
    else setError(res.error.message);
    setUnmaskConfirming(false);
    setUnmasking(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Report detail"
        subtitle="Manage triage, investigation and corrective actions"
        icon={<AlertTriangle className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/hr/whs/reports')}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft className="h-4 w-4" /> Back to reports
        </button>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : !report ? (
          <p className="text-sm text-neutral-500">Report not found.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Overview */}
              <div className={cardCls}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-neutral-400">{report.tracking_code}</span>
                      <Badge label={REPORT_TYPE_LABEL[report.report_type]} />
                      {report.is_notifiable && <Badge label="Notifiable" className="bg-red-100 text-red-800" />}
                    </div>
                    <h2 className="mt-1 text-xl font-semibold text-neutral-900">{report.title}</h2>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <SeverityBadge severity={report.severity} />
                    <StatusBadge status={report.status} />
                  </div>
                </div>

                <p className="mt-4 whitespace-pre-wrap text-sm text-neutral-700">{report.description}</p>

                <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-xs text-neutral-500">Occurred</dt>
                    <dd className="text-neutral-900">{fmtDateTime(report.occurred_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-neutral-500">Reported</dt>
                    <dd className="text-neutral-900">{fmtDateTime(report.reported_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-neutral-500">Location</dt>
                    <dd className="text-neutral-900">{report.location || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-neutral-500">Farm</dt>
                    <dd className="text-neutral-900">{report.farm_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-neutral-500">Severity</dt>
                    <dd className="text-neutral-900">{SEVERITY_LABEL[report.severity]}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-neutral-500">Treatment</dt>
                    <dd className="text-neutral-900">{TREATMENT_LABEL[report.treatment]}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-neutral-500">Injury type</dt>
                    <dd className="text-neutral-900">{report.injury_type || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-neutral-500">Body part</dt>
                    <dd className="text-neutral-900">{report.body_part || '—'}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-neutral-500">Immediate action taken</dt>
                    <dd className="text-neutral-900 whitespace-pre-wrap">{report.immediate_action || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-neutral-500">Reporter</dt>
                    <dd className="text-neutral-900">{report.is_anonymous ? 'Anonymous' : report.reporter_display}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-neutral-500">Regulator notified</dt>
                    <dd className="text-neutral-900">{fmtDateTime(report.notified_regulator_at)}</dd>
                  </div>
                </dl>
              </div>

              {/* Investigation */}
              <div className={cardCls}>
                <div className="flex items-center gap-2 mb-4">
                  <Search className="h-4 w-4 text-neutral-500" />
                  <h3 className="text-sm font-semibold text-neutral-900">Investigation</h3>
                </div>
                {!report.investigation ? (
                  <div>
                    <p className="text-sm text-neutral-500 mb-3">No investigation has been started for this report.</p>
                    <Button size="sm" onClick={startInvestigation} isLoading={saving} disabled={saving}>
                      <Plus className="h-4 w-4" /> Start investigation
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls} htmlFor="inv_method">Method</label>
                        <select id="inv_method" className={inputCls} value={invMethod} onChange={(e) => setInvMethod(e.target.value as InvestigationMethod)}>
                          {INV_METHODS.map((m) => <option key={m} value={m}>{INVESTIGATION_METHOD_LABEL[m]}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="inv_status">Status</label>
                        <select id="inv_status" className={inputCls} value={invStatus} onChange={(e) => setInvStatus(e.target.value as InvestigationStatus)}>
                          {INV_STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="inv_immediate">Immediate causes</label>
                      <textarea id="inv_immediate" className={inputCls} rows={2} value={invImmediate} onChange={(e) => setInvImmediate(e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="inv_contributing">Contributing factors</label>
                      <textarea id="inv_contributing" className={inputCls} rows={2} value={invContributing} onChange={(e) => setInvContributing(e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="inv_root">Root causes</label>
                      <textarea id="inv_root" className={inputCls} rows={2} value={invRoot} onChange={(e) => setInvRoot(e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="inv_findings">Findings</label>
                      <textarea id="inv_findings" className={inputCls} rows={2} value={invFindings} onChange={(e) => setInvFindings(e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="inv_lessons">Lessons learned</label>
                      <textarea id="inv_lessons" className={inputCls} rows={2} value={invLessons} onChange={(e) => setInvLessons(e.target.value)} />
                    </div>
                    <Button size="sm" onClick={saveInvestigation} isLoading={saving} disabled={saving}>Save investigation</Button>
                  </div>
                )}
              </div>

              {/* Corrective actions */}
              <div className={cardCls}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4 text-neutral-500" />
                    <h3 className="text-sm font-semibold text-neutral-900">Corrective actions</h3>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => setShowActionForm((v) => !v)}>
                    <Plus className="h-4 w-4" /> Add action
                  </Button>
                </div>

                {showActionForm && (
                  <div className="mb-4 rounded-lg border border-neutral-200 p-4 space-y-3">
                    <div>
                      <label className={labelCls} htmlFor="a_title">Title</label>
                      <input id="a_title" className={inputCls} value={aTitle} onChange={(e) => setATitle(e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="a_desc">Description</label>
                      <textarea id="a_desc" className={inputCls} rows={2} value={aDesc} onChange={(e) => setADesc(e.target.value)} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls} htmlFor="a_type">Action type</label>
                        <select id="a_type" className={inputCls} value={aType} onChange={(e) => setAType(e.target.value as ActionType)}>
                          {ACTION_TYPES.map((t) => <option key={t} value={t}>{ACTION_TYPE_LABEL[t]}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="a_priority">Priority</label>
                        <select id="a_priority" className={inputCls} value={aPriority} onChange={(e) => setAPriority(e.target.value as ActionPriority)}>
                          {ACTION_PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="a_hierarchy">Hierarchy of control</label>
                        <select id="a_hierarchy" className={inputCls} value={aHierarchy} onChange={(e) => setAHierarchy(e.target.value as HierarchyControl | '')}>
                          <option value="">— Not set —</option>
                          {HIERARCHY_KEYS.map((h) => <option key={h} value={h}>{HIERARCHY_LABEL[h]}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="a_due">Due date</label>
                        <input id="a_due" type="date" className={inputCls} value={aDue} onChange={(e) => setADue(e.target.value)} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelCls} htmlFor="a_assigned">Assign to</label>
                        <select id="a_assigned" className={inputCls} value={aAssigned} onChange={(e) => setAAssigned(e.target.value)}>
                          <option value="">— Unassigned —</option>
                          {users.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={createAction} isLoading={saving} disabled={saving}>Create action</Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowActionForm(false)}>Cancel</Button>
                    </div>
                  </div>
                )}

                {report.actions && report.actions.length > 0 ? (
                  <div className="space-y-3">
                    {report.actions.map((a) => (
                      <div key={a.id} className="rounded-lg border border-neutral-200 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-neutral-900">{a.title}</p>
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500">
                              <span>Due: {fmtDate(a.due_date)}</span>
                              <span>{actionAssignee(a)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge label={PRIORITY_LABEL[a.priority]} className={PRIORITY_CLASS[a.priority]} />
                            <Badge label={ACTION_STATUS_LABEL[a.status]} className={ACTION_STATUS_CLASS[a.status]} />
                          </div>
                        </div>
                        {(a.status === 'open' || a.status === 'in_progress' || a.status === 'completed') && (
                          <div className="mt-3 flex items-center gap-2">
                            {a.status !== 'completed' && (
                              <Button size="sm" variant="secondary" onClick={() => setActionStatus(a.id, 'completed')} disabled={saving}>
                                Mark completed
                              </Button>
                            )}
                            {a.status === 'completed' && (
                              <Button size="sm" variant="secondary" onClick={() => setActionStatus(a.id, 'verified')} disabled={saving}>
                                Mark verified
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-400">No corrective actions yet.</p>
                )}
              </div>

              {/* Updates timeline */}
              <div className={cardCls}>
                <h3 className="text-sm font-semibold text-neutral-900 mb-4">Updates & notes</h3>
                {report.updates && report.updates.length > 0 ? (
                  <ol className="space-y-3 mb-5">
                    {report.updates.map((u, i) => {
                      const author = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
                      return (
                        <li key={u.id ?? i} className="border-l-2 border-neutral-200 pl-4">
                          <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                            <span className="font-medium text-neutral-700">{author || 'System'}</span>
                            {u.new_status && (
                              <span>→ {REPORT_STATUS_LABEL[u.new_status as keyof typeof REPORT_STATUS_LABEL] ?? u.new_status}</span>
                            )}
                            {u.visible_to_reporter && <Badge label="Visible to reporter" className="bg-sky-100 text-sky-800" />}
                            <span>{fmtDateTime(u.created_at)}</span>
                          </div>
                          {u.body && <p className="mt-1 text-sm text-neutral-700 whitespace-pre-wrap">{u.body}</p>}
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <p className="text-sm text-neutral-400 mb-5">No updates yet.</p>
                )}

                <div className="border-t border-neutral-100 pt-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <MessageSquarePlus className="h-4 w-4 text-neutral-500" />
                    <span className="text-sm font-medium text-neutral-900">Add note</span>
                  </div>
                  <textarea
                    className={inputCls}
                    rows={3}
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                    placeholder="Add an internal note or an update for the reporter"
                  />
                  <label className="flex items-center gap-2 text-sm text-neutral-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                      checked={noteVisible}
                      onChange={(e) => setNoteVisible(e.target.checked)}
                    />
                    Visible to reporter
                  </label>
                  <Button size="sm" onClick={addNote} isLoading={saving} disabled={saving || !noteBody.trim()}>Add note</Button>
                </div>
              </div>
            </div>

            {/* Side column: management actions */}
            <div className="space-y-6">
              <div className={cardCls}>
                <h3 className="text-sm font-semibold text-neutral-900 mb-4">Change status</h3>
                <div className="space-y-3">
                  <select className={inputCls} value={statusValue} onChange={(e) => setStatusValue(e.target.value as ReportStatus)} aria-label="Status">
                    {REPORT_STATUSES.map((s) => <option key={s} value={s}>{REPORT_STATUS_LABEL[s]}</option>)}
                  </select>
                  <textarea
                    className={inputCls}
                    rows={2}
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    placeholder="Optional status note"
                  />
                  <label className="flex items-center gap-2 text-sm text-neutral-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                      checked={statusVisible}
                      onChange={(e) => setStatusVisible(e.target.checked)}
                    />
                    Visible to reporter
                  </label>
                  <Button size="sm" onClick={saveStatus} isLoading={saving} disabled={saving}>Update status</Button>
                </div>
              </div>

              <div className={cardCls}>
                <h3 className="text-sm font-semibold text-neutral-900 mb-4">Assignment</h3>
                <div className="space-y-3">
                  <select className={inputCls} value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} aria-label="Assignee">
                    <option value="">— Unassigned —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
                  </select>
                  <Button size="sm" variant="secondary" onClick={saveAssignee} isLoading={saving} disabled={saving}>Save assignee</Button>
                </div>
              </div>

              <div className={cardCls}>
                <h3 className="text-sm font-semibold text-neutral-900 mb-4">Notifiable event</h3>
                <label className="flex items-start gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    checked={report.is_notifiable}
                    onChange={(e) => toggleNotifiable(e.target.checked)}
                    disabled={saving}
                  />
                  Notifiable to the WHS regulator
                </label>
                <p className="mt-3 text-xs text-neutral-500">
                  Regulator notified: {fmtDateTime(report.notified_regulator_at)}
                </p>
                {!report.notified_regulator_at && (
                  <Button size="sm" variant="secondary" className="mt-3" onClick={markNotified} disabled={saving}>
                    Mark regulator notified
                  </Button>
                )}
              </div>

              {/* Unmask reporter */}
              {report.is_anonymous && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert className="h-4 w-4 text-amber-700" />
                    <h3 className="text-sm font-semibold text-amber-900">Anonymous reporter</h3>
                  </div>
                  {unmaskResult ? (
                    unmaskResult.identity ? (
                      <div className="text-sm text-amber-900 space-y-1">
                        <p className="font-medium">Identity revealed (this access is recorded):</p>
                        <p>Name: {unmaskResult.identity.contact_name
                          || [unmaskResult.identity.first_name, unmaskResult.identity.last_name].filter(Boolean).join(' ')
                          || '—'}</p>
                        <p>Email: {unmaskResult.identity.contact_email || unmaskResult.identity.user_email || '—'}</p>
                        <p>Phone: {unmaskResult.identity.contact_phone || '—'}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-amber-900">No identifying details are available for this report.</p>
                    )
                  ) : unmaskConfirming ? (
                    <div className="space-y-3">
                      <p className="text-xs text-amber-800">
                        Revealing the reporter&rsquo;s identity is an audited action and will be recorded against your account.
                        Only do this where there is a genuine WHS need.
                      </p>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="danger" onClick={revealReporter} isLoading={unmasking} disabled={unmasking}>
                          Confirm reveal
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setUnmaskConfirming(false)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs text-amber-800 mb-3">
                        This report was submitted anonymously. The reporter&rsquo;s identity is masked from managers.
                      </p>
                      <Button size="sm" variant="secondary" onClick={() => setUnmaskConfirming(true)}>
                        <Eye className="h-4 w-4" /> Reveal reporter identity (audited)
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function WhsReportDetail() {
  return <WhsGate><WhsReportDetailInner /></WhsGate>;
}
